import { useRef, useState, forwardRef, useImperativeHandle } from "react"
import { useSelector } from "react-redux"
import { useSession } from "../auth/session"
import { QRCodeSVG } from "qrcode.react"
import { meSelector } from "../redux/globalSlice"
import {
  associateSoftwareToken, verifySoftwareToken, enableSoftwareTokenMfa,
  enableEmailMfa,
  updatePhoneNumber, requestPhoneVerificationCode, verifyPhoneNumber, enableSmsMfa,
} from "../api/cognitoMfa"
import { useSignOut } from "../auth/signOut"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

export interface MfaEnrollModalHandle {
  open: () => void
}

const ISSUER = "Newfangled Audio"

type MfaMethod = "totp" | "email" | "sms"

// Per-environment switch: which enrollment methods the wizard offers, e.g.
// "totp" (staging) or "totp,email" (production). Must match the methods
// enabled on the pool AND the backend's MFA_ACCEPTED_FACTORS (bin/cdk.ts).
// Defaults to authenticator app only.
const ALLOWED_METHODS: MfaMethod[] = (process.env.REACT_APP_MFA_METHODS ?? "totp")
  .split(",")
  .map(m => m.trim().toLowerCase())
  .filter((m): m is MfaMethod => m === "totp" || m === "email" || m === "sms")

type Step = "intro" | "choose" | "setup" | "sms-phone" | "sms-verify" | "done"

// MFA enrollment wizard. Paths after the method chooser: authenticator app
// (associate a software token via QR, verify a code, set as preferred),
// email OTP (just set the preference — the email is already verified), or
// SMS (set phone number, verify it with a texted code, set as preferred).
// All end in a forced global sign-out — sessions created before enrollment
// would otherwise keep refreshing without an MFA challenge.
const MfaEnrollModal = forwardRef<MfaEnrollModalHandle>((_, ref) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<Step>("intro")
  const [method, setMethod] = useState<MfaMethod | undefined>(undefined)
  const [secret, setSecret] = useState<string | undefined>(undefined)
  const [codeText, setCodeText] = useState("")
  const [phoneText, setPhoneText] = useState("")
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const me = useSelector(meSelector)?.data
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const signOut = useSignOut()

  useImperativeHandle(ref, () => ({
    open: () => {
      setStep("intro")
      setMethod(undefined)
      setSecret(undefined)
      setCodeText("")
      setPhoneText("")
      setBusy(false)
      setErrorMessage(undefined)
      dialogRef.current?.showModal()
    },
  }), [])

  const close = () => dialogRef.current?.close()

  const handleStartTotp = async () => {
    setMethod("totp")
    setBusy(true)
    setErrorMessage(undefined)
    try {
      const { SecretCode } = await associateSoftwareToken(accessToken)
      setSecret(SecretCode)
      setStep("setup")
    } catch (err: any) {
      console.error("AssociateSoftwareToken failed:", err)
      setErrorMessage(err?.message ?? "Could not start MFA setup")
    } finally {
      setBusy(false)
    }
  }

  const handleEnableEmail = async () => {
    setMethod("email")
    setBusy(true)
    setErrorMessage(undefined)
    try {
      await enableEmailMfa(accessToken)
      setStep("done")
    } catch (err: any) {
      console.error("Enabling email MFA failed:", err)
      setErrorMessage(err?.message ?? "Could not enable email MFA")
    } finally {
      setBusy(false)
    }
  }

  const handleStartSms = () => {
    setMethod("sms")
    setCodeText("")
    setErrorMessage(undefined)
    setStep("sms-phone")
  }

  // Continue from the intro: skip the chooser when only one method is allowed.
  const handleContinue = () => {
    if (ALLOWED_METHODS.length === 1) {
      const only = ALLOWED_METHODS[0]
      if (only === "totp") handleStartTotp()
      else if (only === "email") handleEnableEmail()
      else handleStartSms()
    } else {
      setStep("choose")
    }
  }

  const codeValid = /^\d{6}$/.test(codeText)
  const phoneValid = /^\+[1-9]\d{6,14}$/.test(phoneText)

  const handleVerify = async () => {
    if (!codeValid) return
    setBusy(true)
    setErrorMessage(undefined)
    try {
      const { Status } = await verifySoftwareToken(accessToken, codeText)
      if (Status !== "SUCCESS")
        throw new Error("Code verification failed")
      await enableSoftwareTokenMfa(accessToken)
      setStep("done")
    } catch (err: any) {
      console.error("MFA verification failed:", err)
      setErrorMessage(
        err?.name === "CodeMismatchException" || err?.name === "EnableSoftwareTokenMFAException"
          ? "That code didn't match. Check your authenticator app and try again."
          : err?.message ?? "Verification failed"
      )
    } finally {
      setBusy(false)
    }
  }

  const handleSmsPhoneSubmit = async () => {
    if (!phoneValid) return
    setBusy(true)
    setErrorMessage(undefined)
    try {
      await updatePhoneNumber(accessToken, phoneText)
      await requestPhoneVerificationCode(accessToken)
      setCodeText("")
      setStep("sms-verify")
    } catch (err: any) {
      console.error("Setting phone number failed:", err)
      setErrorMessage(err?.message ?? "Could not send a verification code to that number")
    } finally {
      setBusy(false)
    }
  }

  const handleSmsVerify = async () => {
    if (!codeValid) return
    setBusy(true)
    setErrorMessage(undefined)
    try {
      await verifyPhoneNumber(accessToken, codeText)
      await enableSmsMfa(accessToken)
      setStep("done")
    } catch (err: any) {
      console.error("Phone verification failed:", err)
      setErrorMessage(
        err?.name === "CodeMismatchException"
          ? "That code didn't match. Check the text message and try again."
          : err?.message ?? "Verification failed"
      )
    } finally {
      setBusy(false)
    }
  }

  // Global: every device's refresh tokens are revoked (pre-enrollment
  // sessions would otherwise keep working for 30 days), then the local
  // session is cleared and the user lands on the login page.
  const handleSignOut = async () => {
    setBusy(true)
    await signOut({ global: true })
  }

  const account = me?.email ?? me?.id ?? ""
  const otpauthUrl = secret
    ? `otpauth://totp/${encodeURIComponent(ISSUER)}:${encodeURIComponent(account)}?secret=${secret}&issuer=${encodeURIComponent(ISSUER)}`
    : undefined

  const doneHint =
    method === "email" ? "a code sent to your email"
    : method === "sms" ? "a code texted to your phone"
    : "a code from your authenticator app"

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      {...backdropDismiss(() => {
        // Once MFA is active the only way out is the sign-out button.
        if (step !== "done") close()
      })}
      onCancel={(e) => { if (step === "done") e.preventDefault() }}
    >
      <div className="redeem-dialog-content">
        {step !== "done" && (
          <button className="redeem-dialog-close" onClick={close} aria-label="Close">&times;</button>
        )}
        <h2 className="redeem-dialog-title">Enable Two-Factor Authentication</h2>

        {step === "intro" && (
          <>
            <p>
              {ALLOWED_METHODS.length === 1
                ? "You'll need an authenticator app (Google Authenticator, 1Password, Authy, …) on your phone or computer."
                : "You can verify sign-ins with an authenticator app or another available method."}
            </p>
            <p>
              After setup you'll be signed out everywhere and will need to sign
              back in with a verification code.
            </p>
            {errorMessage && <p className="generate-codes-error">{errorMessage}</p>}
            <div className="redeem-actions">
              <button onClick={close} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" onClick={handleContinue} disabled={busy}>
                {busy ? <><Spinner size={12} /> Starting…</> : "Continue"}
              </button>
            </div>
          </>
        )}

        {step === "choose" && (
          <>
            <p>How do you want to receive verification codes?</p>
            <div className="mfa-method-options">
              {ALLOWED_METHODS.includes("totp") && (
                <button className="mfa-method-option" onClick={handleStartTotp} disabled={busy}>
                  <strong>Authenticator app</strong>
                  <span className="generate-codes-hint">
                    Google Authenticator, 1Password, Authy, … Recommended.
                  </span>
                </button>
              )}
              {ALLOWED_METHODS.includes("email") && (
                <button className="mfa-method-option" onClick={handleEnableEmail} disabled={busy}>
                  <strong>Email code</strong>
                  <span className="generate-codes-hint">
                    A code sent to {account || "your email"} at each sign-in.
                  </span>
                </button>
              )}
              {ALLOWED_METHODS.includes("sms") && (
                <button className="mfa-method-option" onClick={handleStartSms} disabled={busy}>
                  <strong>Text message (SMS)</strong>
                  <span className="generate-codes-hint">
                    A code texted to your phone at each sign-in.
                  </span>
                </button>
              )}
            </div>
            {busy && <p><Spinner size={12} /> Setting up…</p>}
            {errorMessage && <p className="generate-codes-error">{errorMessage}</p>}
            <div className="redeem-actions">
              <button onClick={close} disabled={busy}>Cancel</button>
            </div>
          </>
        )}

        {step === "setup" && otpauthUrl && (
          <>
            <p>Scan this code with your authenticator app:</p>
            <div className="mfa-qr">
              <QRCodeSVG value={otpauthUrl} size={168} marginSize={2} />
              <span className="generate-codes-hint">
                Can't scan? Enter this key manually:
              </span>
              <span className="mfa-secret">{secret}</span>
            </div>
            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">Enter the 6-digit code from the app</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={codeText}
                  onChange={(e) => setCodeText(e.target.value.replace(/\D/g, ""))}
                  className="generate-codes-input"
                  disabled={busy}
                  autoFocus
                />
              </label>
            </div>
            {errorMessage && <p className="generate-codes-error">{errorMessage}</p>}
            <div className="redeem-actions">
              <button onClick={close} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" onClick={handleVerify} disabled={!codeValid || busy}>
                {busy ? <><Spinner size={12} /> Verifying…</> : "Verify & Enable"}
              </button>
            </div>
          </>
        )}

        {step === "sms-phone" && (
          <>
            <p>Enter the phone number that should receive sign-in codes:</p>
            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">Phone number (with country code, e.g. +12125551234)</span>
                <input
                  type="tel"
                  autoComplete="tel"
                  value={phoneText}
                  onChange={(e) => setPhoneText(e.target.value.replace(/[^+\d]/g, ""))}
                  className="generate-codes-input"
                  disabled={busy}
                  autoFocus
                />
              </label>
            </div>
            {errorMessage && <p className="generate-codes-error">{errorMessage}</p>}
            <div className="redeem-actions">
              <button onClick={close} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" onClick={handleSmsPhoneSubmit} disabled={!phoneValid || busy}>
                {busy ? <><Spinner size={12} /> Sending…</> : "Send Code"}
              </button>
            </div>
          </>
        )}

        {step === "sms-verify" && (
          <>
            <p>We texted a code to <strong>{phoneText}</strong>.</p>
            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">Enter the 6-digit code from the text message</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={codeText}
                  onChange={(e) => setCodeText(e.target.value.replace(/\D/g, ""))}
                  className="generate-codes-input"
                  disabled={busy}
                  autoFocus
                />
              </label>
            </div>
            {errorMessage && <p className="generate-codes-error">{errorMessage}</p>}
            <div className="redeem-actions">
              <button onClick={close} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" onClick={handleSmsVerify} disabled={!codeValid || busy}>
                {busy ? <><Spinner size={12} /> Verifying…</> : "Verify & Enable"}
              </button>
            </div>
          </>
        )}

        {step === "done" && (
          <>
            <p className="generate-codes-success">
              Two-factor authentication is now <strong>enabled</strong>.
            </p>
            <p>
              You'll now be signed out everywhere. Sign back in with your
              password and {doneHint}.
            </p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={handleSignOut} disabled={busy}>
                {busy ? <><Spinner size={12} /> Signing out…</> : "Sign Out"}
              </button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
})

export default MfaEnrollModal
